import { COST_CATEGORIES, estimateLineInput, type CostCategory, type EstimateLineInput } from '@buildbee/contracts';

/**
 * Estimate line import from a CSV (a takeoff tool's export, a supplier quote, a spreadsheet).
 * The browser parses the file (papaparse), the estimator maps columns to fields, the rows are
 * previewed with these rules, and the API re-validates every row with the same rules before it
 * appends them to the estimate in one atomic batch.
 */

/** Rows one import may append (keeps the batch inside D1's per-invocation statement budget). */
export const ESTIMATE_LINE_IMPORT_MAX_ROWS = 1000;

export const ESTIMATE_LINE_IMPORT_FIELDS = [
  { key: 'description', label: 'Description', required: true },
  { key: 'quantity', label: 'Quantity', required: false },
  { key: 'unit', label: 'Unit', required: false },
  { key: 'unitCost', label: 'Unit cost', required: false },
  { key: 'costCode', label: 'Cost code', required: false },
  { key: 'section', label: 'Section', required: false },
  { key: 'category', label: 'Category', required: false },
  { key: 'markup', label: 'Markup %', required: false },
  { key: 'notes', label: 'Notes', required: false },
] as const;
export type EstimateLineImportField = (typeof ESTIMATE_LINE_IMPORT_FIELDS)[number]['key'];
export const ESTIMATE_LINE_IMPORT_FIELD_KEYS = ESTIMATE_LINE_IMPORT_FIELDS.map((f) => f.key) as readonly EstimateLineImportField[];

export type EstimateLineImportPreset = {
  id: 'active_takeoff' | 'generic';
  label: string;
  /** Header aliases per field, best first. The first unused header that matches wins. */
  aliases: Partial<Record<EstimateLineImportField, string[]>>;
};

/**
 * Active Takeoff's quantity export. ASSUMPTIONS (the export is configurable and varies by version
 * and by the report chosen): one row per condition/item with its takeoff quantity and unit of
 * measure, e.g. `Condition/Item, Quantity, UOM, Cost Code, Description[, Unit Cost, Folder, Type]`.
 * "Condition/Item" (or "Condition" / "Item") is the line description and a separate free-text
 * "Description" column becomes the line note; "Folder"/"Group" becomes the section. Headings and
 * subtotal rows without a quantity show as errors in the preview and can be skipped. Anything the
 * aliases miss can be mapped by hand in the mapping step.
 */
export const ACTIVE_TAKEOFF_PRESET: EstimateLineImportPreset = {
  id: 'active_takeoff',
  label: 'Active Takeoff export',
  aliases: {
    description: ['condition/item', 'condition / item', 'condition', 'item', 'item name', 'name', 'description'],
    quantity: ['quantity', 'qty', 'takeoff quantity', 'total quantity', 'quantity 1', 'qty1'],
    unit: ['uom', 'unit of measure', 'unit', 'units', 'uom 1'],
    costCode: ['cost code', 'costcode', 'code', 'item code', 'phase code'],
    unitCost: ['unit cost', 'unit price', 'cost/unit', 'cost per unit', 'rate', 'price'],
    section: ['folder', 'group', 'section', 'area', 'division'],
    category: ['type', 'cost type', 'category'],
    markup: ['markup', 'markup %', 'markup percent'],
    notes: ['description', 'notes', 'note', 'comments'],
  },
};

/** Any spreadsheet with sensible headers. */
export const GENERIC_ESTIMATE_LINE_PRESET: EstimateLineImportPreset = {
  id: 'generic',
  label: 'Generic spreadsheet',
  aliases: {
    description: ['description', 'item', 'item description', 'name', 'line item', 'scope', 'task'],
    quantity: ['quantity', 'qty', 'quan', 'amount qty', 'count'],
    unit: ['unit', 'units', 'uom', 'unit of measure', 'u/m'],
    unitCost: ['unit cost', 'unit price', 'cost', 'rate', 'price', 'cost each', 'unit rate'],
    costCode: ['cost code', 'costcode', 'code', 'csi', 'csi code', 'account'],
    section: ['section', 'group', 'phase', 'division', 'area', 'room'],
    category: ['category', 'type', 'cost type', 'kind'],
    markup: ['markup', 'markup %', 'markup percent', 'margin %'],
    notes: ['notes', 'note', 'comments', 'comment', 'remarks'],
  },
};

export const ESTIMATE_LINE_IMPORT_PRESETS = [ACTIVE_TAKEOFF_PRESET, GENERIC_ESTIMATE_LINE_PRESET] as const;

const headerKey = (h: string) =>
  h
    .trim()
    .toLowerCase()
    .replace(/[_\-.]+/g, ' ')
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s+/g, ' ');

/**
 * Suggested mapping header -> field for a preset. Every header maps to at most one field and
 * every field to at most one header. Unmatched headers are left out (ignored on import).
 */
export function suggestEstimateLineMapping(headers: readonly string[], preset: EstimateLineImportPreset = GENERIC_ESTIMATE_LINE_PRESET): Record<string, EstimateLineImportField> {
  const out: Record<string, EstimateLineImportField> = {};
  const usedHeaders = new Set<string>();
  for (const field of ESTIMATE_LINE_IMPORT_FIELD_KEYS) {
    for (const alias of preset.aliases[field] ?? []) {
      const header = headers.find((h) => !usedHeaders.has(h) && headerKey(h) === headerKey(alias));
      if (header) {
        out[header] = field;
        usedHeaders.add(header);
        break;
      }
    }
  }
  return out;
}

/** Which preset fits a header row best (most fields matched; Active Takeoff wins ties on its tell-tale headers). */
export function detectEstimateLinePreset(headers: readonly string[]): EstimateLineImportPreset {
  const keys = headers.map(headerKey);
  if (keys.some((k) => k === 'condition/item' || k === 'condition') || (keys.includes('uom') && keys.some((k) => k === 'item'))) return ACTIVE_TAKEOFF_PRESET;
  return GENERIC_ESTIMATE_LINE_PRESET;
}

/** Problems with a mapping itself (unknown columns or fields, a field mapped twice, description missing). */
export function estimateLineMappingProblems(headers: readonly string[], mapping: Record<string, string>): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const [header, field] of Object.entries(mapping)) {
    if (!field) continue;
    if (!headers.includes(header)) problems.push(`Column "${header}" is not in the file`);
    if (!(ESTIMATE_LINE_IMPORT_FIELD_KEYS as readonly string[]).includes(field)) problems.push(`Unknown field "${field}"`);
    else if (seen.has(field)) problems.push(`More than one column is mapped to ${labelOf(field)}`);
    seen.add(field);
  }
  if (!seen.has('description')) problems.push('Map a column to Description');
  return problems;
}

const labelOf = (field: string) => ESTIMATE_LINE_IMPORT_FIELDS.find((f) => f.key === field)?.label ?? field;

/**
 * A number as spreadsheets write it: `1,234.5`, `$1,234.50`, `(12.00)` (negative), `12 %`.
 * Returns null for a blank cell and NaN when it is not a number.
 */
export function parseDecimalCell(raw: string): number | null {
  let s = raw.trim();
  if (!s) return null;
  let negative = false;
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1);
  }
  s = s.replace(/[$€£\s,%]/g, '').replace(/^(CAD|USD|CA)/i, '');
  if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(s)) return Number.NaN;
  const n = Number(s);
  return negative ? -n : n;
}

const UNIT_ALIASES: Record<string, string> = {
  each: 'ea',
  ea: 'ea',
  pcs: 'ea',
  pc: 'ea',
  'sq ft': 'sf',
  sqft: 'sf',
  'sq. ft.': 'sf',
  ft2: 'sf',
  'ft²': 'sf',
  'square feet': 'sf',
  'sq yd': 'sy',
  sqyd: 'sy',
  yd2: 'sy',
  'lin ft': 'lf',
  'linear feet': 'lf',
  'ln ft': 'lf',
  'cu yd': 'cy',
  cuyd: 'cy',
  yd3: 'cy',
  'cu ft': 'cf',
  ft3: 'cf',
  'm²': 'm2',
  'sq m': 'm2',
  'm³': 'm3',
  'cu m': 'm3',
  'lump sum': 'ls',
  lumpsum: 'ls',
  hours: 'hr',
  hour: 'hr',
  hrs: 'hr',
};

/** Unit of measure as estimating units are usually written (`Sq Ft` -> `sf`, `Each` -> `ea`); blank is `ea`. */
export function normalizeImportUnit(raw: string | undefined): string {
  const s = (raw ?? '').trim();
  if (!s) return 'ea';
  const k = s.toLowerCase().replace(/\s+/g, ' ');
  return (UNIT_ALIASES[k] ?? k).slice(0, 20);
}

const CATEGORY_ALIASES: Record<string, CostCategory> = {
  labor: 'labor',
  labour: 'labor',
  lab: 'labor',
  l: 'labor',
  material: 'material',
  materials: 'material',
  mat: 'material',
  m: 'material',
  equipment: 'equipment',
  equip: 'equipment',
  eq: 'equipment',
  e: 'equipment',
  subcontract: 'subcontract',
  subcontractor: 'subcontract',
  sub: 'subcontract',
  subs: 'subcontract',
  s: 'subcontract',
  allowance: 'allowance',
  other: 'other',
  o: 'other',
};

export function parseCategoryCell(raw: string | undefined): CostCategory | null {
  const k = (raw ?? '').trim().toLowerCase();
  if (!k) return 'material';
  return CATEGORY_ALIASES[k] ?? ((COST_CATEGORIES as readonly string[]).includes(k) ? (k as CostCategory) : null);
}

export type MappedEstimateLine = Omit<EstimateLineInput, 'position' | 'id'>;
export type EstimateLineRowResult = { ok: true; value: MappedEstimateLine } | { ok: false; errors: string[] };

/**
 * One CSV row as an estimate line. Quantity defaults to 1 when no column is mapped to it (but a
 * mapped, blank quantity is an error: usually a heading or subtotal row). Unit cost is money as
 * written (`$1,250.00`); markup is a percent (`15` or `15%` = 15%), blank = the estimate default.
 */
export function mapEstimateLineRow(headers: readonly string[], row: readonly string[], mapping: Record<string, string>): EstimateLineRowResult {
  const v = new Map<string, string>();
  headers.forEach((h, i) => {
    const field = mapping[h];
    if (field) v.set(field, (row[i] ?? '').trim());
  });
  const errors: string[] = [];
  const description = v.get('description') ?? '';
  if (!description) errors.push('Description is blank');

  let quantity = 1;
  if (v.has('quantity')) {
    const q = parseDecimalCell(v.get('quantity')!);
    if (q === null) errors.push('Quantity is blank');
    else if (Number.isNaN(q)) errors.push(`Quantity "${v.get('quantity')}" is not a number`);
    else quantity = q;
  }

  let unitCostCents = 0;
  const costRaw = v.get('unitCost');
  if (costRaw) {
    const c = parseDecimalCell(costRaw);
    if (c === null || Number.isNaN(c)) errors.push(`Unit cost "${costRaw}" is not an amount`);
    else unitCostCents = Math.round(c * 100);
  }

  let markupBps: number | null = null;
  const markupRaw = v.get('markup');
  if (markupRaw) {
    const m = parseDecimalCell(markupRaw);
    if (m === null || Number.isNaN(m)) errors.push(`Markup "${markupRaw}" is not a percent`);
    else markupBps = Math.round(m * 100);
  }

  const category = parseCategoryCell(v.get('category'));
  if (!category) errors.push(`Category "${v.get('category')}" is not one of ${COST_CATEGORIES.join(', ')}`);

  if (errors.length) return { ok: false, errors };
  const parsed = estimateLineInput.safeParse({
    position: 0,
    description,
    quantity,
    unit: normalizeImportUnit(v.get('unit')),
    unitCostCents,
    markupBps,
    costCode: v.get('costCode') || null,
    section: v.get('section') || null,
    category: category!,
    notes: v.get('notes') || null,
  });
  if (!parsed.success) return { ok: false, errors: parsed.error.issues.map((i) => (i.path.length ? `${labelOf(String(i.path[0]))}: ${i.message}` : i.message)) };
  const { position: _p, id: _id, ...value } = parsed.data;
  return { ok: true, value };
}

export type EstimateLineImportPreview = {
  rows: { row: number; ok: boolean; errors: string[]; value: MappedEstimateLine | null }[];
  valid: number;
  invalid: number;
};

/** Validate every row (row numbers count the header as row 1, like a spreadsheet). */
export function previewEstimateLineImport(headers: readonly string[], rows: readonly (readonly string[])[], mapping: Record<string, string>): EstimateLineImportPreview {
  const out = rows.map((r, i) => {
    const m = mapEstimateLineRow(headers, r, mapping);
    return m.ok ? { row: i + 2, ok: true, errors: [], value: m.value } : { row: i + 2, ok: false, errors: m.errors, value: null };
  });
  const valid = out.filter((r) => r.ok).length;
  return { rows: out, valid, invalid: out.length - valid };
}
